// HomePage.jsx  ─  Animac Metaverse  ─  Production-grade redesign
// Requires: framer-motion, lucide-react, react-router-dom, axios
// External hooks: useArticles, useFeaturedContent  (from ../hooks/useArticles)
// External components: SEO, ContentRow, WatchTowerPreview, CommunityEvents, CommunityFeatures

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, ChevronRight, Play,
  Flame, Globe, Sparkles, TrendingUp,
  BookOpen, Star, Clock, Users, Tv,
  Mail, ExternalLink
} from 'lucide-react';
import SEO from '../components/SEO';
import ContentRow from '../components/ContentRow';
import WatchTowerPreview from '../components/StreamfinitePreview';
import CommunityEvents from '../components/CommunityEvents';
import CommunityFeatures from '../components/CommunityFeatures';
import { useFeaturedContent, useArticles } from '../hooks/useArticles';
import HeroSection from '../components/HeroSection';

/* ═══════════════════════════════════════════════════════════════════
   GLOBAL STYLES
═══════════════════════════════════════════════════════════════════ */
const PageStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+JP:wght@700;900&display=swap');
    @font-face { font-family:'Azonix'; src:url('/fonts/Azonix.otf') format('opentype'),url('/fonts/azonix.woff2') format('woff2'); font-display:swap; }
    @font-face { font-family:'Japanese3017'; src:url('/fonts/Japanese-3017.ttf') format('truetype'); font-display:swap; }
    @font-face { font-family:'Acknowledgement'; src:url('/fonts/Acknowledgement.otf') format('opentype'); font-display:swap; }

    :root {
      --az: 'Azonix','Impact',sans-serif;
      --jp: 'Japanese3017','Noto Serif JP',serif;
      --wt: 'Acknowledgement','Azonix',sans-serif;
      --ui: -apple-system,'Helvetica Neue',sans-serif;
      --bd: 'Georgia',serif;
      --east: #dc2626;
      --west: #3b82f6;
      --neu:  #a78bfa;
      --bg:   #08080c;
      --glass: rgba(255,255,255,0.04);
      --gb:    rgba(255,255,255,0.08);
    }

    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
    html{scroll-behavior:smooth;}

    .hp-root {
      background: var(--bg);
      color: #fff;
      font-family: var(--ui);
      overflow-x: hidden;
      min-height: 100vh;
      position: relative;
    }

    /* ── CONTAINER ── */
    .hp-wrap {
      max-width: 1240px;
      margin: 0 auto;
      padding: 0 clamp(16px,4vw,52px);
      position: relative;
      z-index: 1;
    }

    /* ── SECTION SPACING ── */
    .hp-sec       { padding: clamp(56px,8vh,100px) 0; }
    .hp-sec-sm    { padding: clamp(36px,5vh,64px) 0; }
    .hp-sec-top   { padding-top: clamp(56px,8vh,100px); }

    /* ── SECTION HEADER ── */
    .hp-sh {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 28px;
      gap: 16px;
    }
    .hp-sh-left { display:flex; align-items:center; gap:12px; }
    .hp-sh-bar  { width:3px; height:26px; border-radius:2px; flex-shrink:0; }
    .hp-sh-label {
      font-family: var(--az);
      font-size: 16px;
      letter-spacing: 1px;
      text-transform: uppercase;
    }
    .hp-sh-link {
      display: flex; align-items: center; gap: 5px;
      font-size: 10px; color: rgba(255,255,255,0.28);
      text-decoration: none; letter-spacing:1px;
      font-family: var(--ui); transition: color .2s;
      white-space: nowrap;
    }
    .hp-sh-link:hover { color:#fff; }

    /* ── DIVIDER ── */
    .hp-div {
      height: 1px;
      background: linear-gradient(to right, transparent, rgba(255,255,255,0.06), transparent);
    }

    /* ═══════════════════════════════════════
       PORTAL CARDS
    ═══════════════════════════════════════ */
    .hp-portals {
      display: grid;
      grid-template-columns: repeat(2,1fr);
      gap: 14px;
    }
    @media(max-width:680px){ .hp-portals { grid-template-columns:1fr; } }

    .hp-portal {
      position: relative;
      border-radius: 22px;
      overflow: hidden;
      cursor: pointer;
      display: block;
      text-decoration: none;
      background: var(--glass);
      backdrop-filter: blur(24px);
      -webkit-backdrop-filter: blur(24px);
      border: 1px solid var(--gb);
      transition: all .42s cubic-bezier(.4,0,.2,1);
      padding: clamp(22px,3vw,32px);
    }
    .hp-portal:hover { transform: translateY(-6px); }
    .hp-portal-east:hover { border-color: rgba(220,38,38,.3); box-shadow: 0 28px 80px rgba(220,38,38,.12), 0 0 0 1px rgba(220,38,38,.08); }
    .hp-portal-west:hover { border-color: rgba(6, 85, 212, 0.3); box-shadow: 0 28px 80px rgba(6,182,212,.12), 0 0 0 1px rgba(6,182,212,.08); }

    .hp-portal-accent-line {
      position: absolute; top:0; left:8%; right:8%; height:1px;
      opacity:.5;
    }
    .hp-portal-tint {
      position: absolute; inset:0; pointer-events:none; border-radius:22px;
    }

    .hp-portal-title-east {
      font-family: var(--jp);
      font-size: clamp(37px,7vw,59px);
      font-weight:900; color:var(--east);
      text-shadow: 0 0 40px rgba(220,38,38,.4);
      line-height:1;
    }
    .hp-portal-title-west {
      font-family: var(--wt);
      font-size: clamp(37px,7vw,59px);
      color:var(--west);
      text-shadow: 0 0 40px rgba(6, 85, 212, 0.4);
      line-height:1;
    }

    .hp-portal-stats {
      display: grid; grid-template-columns: repeat(3,1fr);
      gap:1px; background:rgba(255,255,255,.06);
      border-radius:11px; overflow:hidden; margin-bottom:18px;
    }
    .hp-portal-stat {
      padding:11px 8px; text-align:center;
      background:rgba(0,0,0,.28);
    }
    .hp-portal-cta {
      display:flex; align-items:center; justify-content:center; gap:8px;
      padding:12px; border-radius:11px;
      font-family:var(--az); font-size:10px;
      letter-spacing:2px; text-transform:uppercase;
    }

    /* ═══════════════════════════════════════
       STREAMFINITE PROMO BANNER
    ═══════════════════════════════════════ */
    .hp-stream-banner {
      position: relative;
      border-radius: 22px;
      overflow: hidden;
      padding: clamp(28px,4vw,48px);
      background: linear-gradient(135deg, rgba(99,102,241,.12) 0%, rgba(168,85,247,.08) 50%, rgba(236,72,153,.06) 100%);
      border: 1px solid rgba(99,102,241,.2);
      display: grid;
      grid-template-columns: 1fr auto;
      gap: 24px;
      align-items: center;
    }
    @media(max-width:640px){ .hp-stream-banner { grid-template-columns:1fr; } }
    .hp-stream-banner-glow {
      position: absolute; inset:0; pointer-events:none;
      background: radial-gradient(ellipse 60% 60% at 80% 50%, rgba(99,102,241,.1), transparent);
    }
    .hp-stream-eyebrow {
      font-family: var(--az); font-size:9px; letter-spacing:3px;
      text-transform:uppercase; color:var(--neu); margin-bottom:10px;
    }
    .hp-stream-title {
      font-family: var(--az);
      font-size: clamp(22px,3.5vw,38px);
      line-height:1.1; color:#fff; margin-bottom:10px;
    }
    .hp-stream-desc {
      font-size:14px; color:rgba(255,255,255,.42);
      line-height:1.7; max-width:460px; font-family:var(--bd);
    }
    .hp-stream-badge-row {
      display:flex; gap:8px; flex-wrap:wrap; margin-top:20px;
    }
    .hp-stream-badge {
      display:flex; align-items:center; gap:6px;
      padding:6px 12px; border-radius:100px;
      background:rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.1);
      font-size:10px; color:rgba(255,255,255,.6); font-family:var(--ui);
    }
    .hp-stream-enter {
      display:inline-flex; align-items:center; gap:8px;
      padding:13px 26px; border-radius:11px; text-decoration:none;
      background:linear-gradient(135deg,#6366f1,#8b5cf6);
      color:#fff; font-family:var(--az); font-size:10px;
      letter-spacing:1.5px; text-transform:uppercase;
      box-shadow: 0 8px 32px rgba(99,102,241,.4);
      transition: all .25s; white-space:nowrap; flex-shrink:0;
    }
    .hp-stream-enter:hover { box-shadow:0 12px 44px rgba(99,102,241,.6); transform:translateY(-2px); }

    /* ═══════════════════════════════════════
       FEATURED ARTICLE CARDS
    ═══════════════════════════════════════ */
    .hp-feature-grid {
      display:grid; grid-template-columns:1fr 280px; gap:12px; align-items:start;
    }
    .hp-feature-grid-rev { grid-template-columns:280px 1fr; }
    @media(max-width:860px){
      .hp-feature-grid, .hp-feature-grid-rev { grid-template-columns:1fr; }
    }

    .hp-hero-card {
      position:relative; border-radius:18px; overflow:hidden;
      height:clamp(260px,35vw,440px);
      display:block; text-decoration:none; cursor:pointer;
    }
    .hp-hero-card img {
      position:absolute; inset:0; width:100%; height:100%;
      object-fit:cover; transition:transform .7s ease;
    }
    .hp-hero-card:hover img { transform:scale(1.04); }
    .hp-hero-card-grad {
      position:absolute; inset:0;
      background:linear-gradient(to top,rgba(0,0,0,.92) 0%,rgba(0,0,0,.28) 50%,transparent 100%);
    }
    .hp-hero-card-body { position:absolute; bottom:0; left:0; right:0; padding:22px; }
    .hp-hero-card-cat {
      font-family:var(--az); font-size:8px; font-weight:700;
      letter-spacing:2.5px; text-transform:uppercase; margin-bottom:8px; display:block;
    }
    .hp-hero-card-title {
      font-family:var(--az); font-size:clamp(14px,2.2vw,21px);
      line-height:1.2; color:#fff; margin-bottom:10px;
      display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden;
    }
    .hp-hero-card-meta {
      display:flex; gap:12px; font-size:10px;
      color:rgba(255,255,255,.38); font-family:var(--ui);
    }
    .hp-hero-card-meta span { display:flex; align-items:center; gap:4px; }
    .hp-feat-badge {
      position:absolute; top:14px; left:14px;
      font-family:var(--az); font-size:8px; font-weight:700;
      letter-spacing:2px; padding:4px 11px; border-radius:100px; text-transform:uppercase;
    }

    /* side cards stack */
    .hp-side-col { display:flex; flex-direction:column; gap:9px; }

    .hp-side-card {
      display:flex; border-radius:12px; overflow:hidden;
      background:var(--glass); border:1px solid var(--gb);
      text-decoration:none;
      backdrop-filter:blur(20px); -webkit-backdrop-filter:blur(20px);
      transition:all .28s ease;
    }
    .hp-side-card:hover {
      background:rgba(255,255,255,.07); border-color:rgba(255,255,255,.14);
      transform:translateX(4px);
    }
    .hp-side-thumb {
      width:84px; min-width:84px; height:72px;
      position:relative; overflow:hidden; flex-shrink:0;
    }
    .hp-side-thumb img { width:100%; height:100%; object-fit:cover; }
    .hp-side-rank {
      position:absolute; top:5px; left:5px;
      width:18px; height:18px; border-radius:50%;
      display:flex; align-items:center; justify-content:center;
      font-size:8px; font-weight:800; color:#fff; font-family:var(--az);
    }
    .hp-side-body { padding:9px 12px; flex:1; overflow:hidden; }
    .hp-side-title {
      font-size:11px; font-weight:600; color:#fff; line-height:1.4;
      display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical;
      overflow:hidden; font-family:var(--ui); margin-bottom:3px;
    }

    /* ═══════════════════════════════════════
       GRID CARDS (browse/all)
    ═══════════════════════════════════════ */
    .hp-grid { display:grid; gap:10px; }
    .hp-grid-4 { grid-template-columns:repeat(4,1fr); }
    .hp-grid-3 { grid-template-columns:repeat(3,1fr); }
    @media(max-width:960px){ .hp-grid-4 { grid-template-columns:repeat(2,1fr); } }
    @media(max-width:640px){ .hp-grid-4,.hp-grid-3 { grid-template-columns:repeat(2,1fr); } }
    @media(max-width:380px){ .hp-grid-4,.hp-grid-3 { grid-template-columns:1fr; } }

    .hp-grid-card {
      border-radius:13px; overflow:hidden;
      background:var(--glass); border:1px solid var(--gb);
      text-decoration:none; display:block;
      backdrop-filter:blur(16px); -webkit-backdrop-filter:blur(16px);
      transition:all .28s ease;
    }
    .hp-grid-card:hover {
      background:rgba(255,255,255,.07); border-color:rgba(255,255,255,.14);
      transform:translateY(-4px); box-shadow:0 18px 56px rgba(0,0,0,.44);
    }
    .hp-grid-card-img {
      position:relative; aspect-ratio:16/9; overflow:hidden;
    }
    .hp-grid-card-img img {
      width:100%; height:100%; object-fit:cover; transition:transform .5s ease;
    }
    .hp-grid-card:hover .hp-grid-card-img img { transform:scale(1.06); }
    .hp-grid-card-body { padding:11px 13px 13px; }
    .hp-grid-card-title {
      font-size:12px; font-weight:600; color:#fff; line-height:1.4;
      display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical;
      overflow:hidden; font-family:var(--ui);
    }

    /* ═══════════════════════════════════════
       TICKER / MARQUEE ANNOUNCEMENT
    ═══════════════════════════════════════ */
    .hp-ticker {
      background: rgba(255,255,255,.03);
      border-top: 1px solid rgba(255,255,255,.05);
      border-bottom: 1px solid rgba(255,255,255,.05);
      overflow: hidden;
      padding: 10px 0;
    }
    @keyframes tickerRoll {
      0%   { transform: translateX(0); }
      100% { transform: translateX(-50%); }
    }
    .hp-ticker-inner {
      display: flex;
      gap: 0;
      animation: tickerRoll 28s linear infinite;
      width: max-content;
    }
    .hp-ticker-inner:hover { animation-play-state: paused; }
    .hp-ticker-item {
      display: inline-flex; align-items: center; gap: 10px;
      padding: 0 32px;
      font-family: var(--az); font-size: 9px; letter-spacing: 2px;
      color: rgba(255,255,255,.35); text-transform: uppercase; white-space: nowrap;
    }
    .hp-ticker-dot { width:4px; height:4px; border-radius:50%; flex-shrink:0; }

    /* ═══════════════════════════════════════
       COMMUNITY PANEL
    ═══════════════════════════════════════ */
    .hp-community-grid {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .hp-comm-card {
      border-radius:18px; overflow:hidden;
    }

    /* ═══════════════════════════════════════
       NEWSLETTER
    ═══════════════════════════════════════ */
    .hp-nl {
      border-radius:22px; overflow:hidden;
      background: linear-gradient(135deg,rgba(220,38,38,.08) 0%,rgba(6,182,212,.06) 100%);
      border: 1px solid rgba(255,255,255,.07);
      padding: clamp(32px,5vw,56px) clamp(24px,4vw,48px);
      position:relative; text-align:center;
    }
    .hp-nl-glow {
      position:absolute; top:50%; left:50%; transform:translate(-50%,-50%);
      width:500px; height:200px; border-radius:50%;
      background:radial-gradient(ellipse,rgba(167,139,250,.08),transparent);
      filter:blur(48px); pointer-events:none;
    }
    .hp-nl-form {
      display:flex; gap:10px; max-width:420px; margin:24px auto 0;
      flex-wrap:wrap;
    }
    .hp-nl-input {
      flex:1; min-width:200px; background:rgba(255,255,255,.06);
      border:1px solid rgba(255,255,255,.1); border-radius:10px;
      padding:11px 16px; color:#fff; font-size:13px; font-family:var(--ui);
      outline:none; transition:border-color .2s;
    }
    .hp-nl-input::placeholder { color:rgba(255,255,255,.25); }
    .hp-nl-input:focus { border-color:rgba(255,255,255,.25); }
    .hp-nl-btn {
      padding:11px 22px; border-radius:10px; border:none; cursor:pointer;
      background:#fff; color:#08080c; font-family:var(--az);
      font-size:10px; letter-spacing:1.5px; text-transform:uppercase;
      transition:all .25s; white-space:nowrap;
    }
    .hp-nl-btn:hover { background:rgba(255,255,255,.88); }

    /* ═══════════════════════════════════════
       FOOTER AREA
    ═══════════════════════════════════════ */
    .hp-footer-cta {
      max-width:660px; margin:0 auto;
      border-radius:22px; text-align:center;
      background:var(--glass); border:1px solid var(--gb);
      backdrop-filter:blur(24px); -webkit-backdrop-filter:blur(24px);
      padding:clamp(32px,5vw,60px) clamp(22px,4vw,40px);
      position:relative; overflow:hidden;
    }
    .hp-footer-cta-glow {
      position:absolute; top:50%; left:50%; transform:translate(-50%,-50%);
      width:320px; height:160px; border-radius:50%;
      background:radial-gradient(ellipse,rgba(167,139,250,.1),transparent);
      filter:blur(40px); pointer-events:none;
    }
    .hp-ghost-link {
      display:inline-flex; align-items:center; gap:7px;
      padding:10px 22px; border-radius:10px; text-decoration:none;
      border:1px solid rgba(167,139,250,.32);
      color:var(--neu); font-size:10px; font-weight:600;
      letter-spacing:1px; transition:all .25s; font-family:var(--az);
    }
    .hp-ghost-link:hover { border-color:rgba(167,139,250,.6); background:rgba(167,139,250,.08); }
  `}</style>
);

/* ═══════════════════════════════════════════════════════════════════
   PARTICLE BACKGROUND  (canvas, mouse-reactive)
═══════════════════════════════════════════════════════════════════ */
const ParticleBackground = () => {
  const ref = useRef(null);
  const mouse = useRef({ x: 0, y: 0 });
  const raf = useRef(null);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    let W = cv.width = window.innerWidth;
    let H = cv.height = window.innerHeight;

    const resize = () => { W = cv.width = window.innerWidth; H = cv.height = window.innerHeight; };
    window.addEventListener('resize', resize);

    const pts = Array.from({ length: 50 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      vx: (Math.random() - .5) * .26, vy: (Math.random() - .5) * .26,
      r: Math.random() * 1.4 + .3, a: Math.random() * .4 + .07,
      h: [218, 255, 198][Math.floor(Math.random() * 3)],
    }));

    const mm = (e) => { mouse.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener('mousemove', mm);

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      const { x: mx, y: my } = mouse.current;
      for (let i = 0; i < pts.length; i++)
        for (let j = i + 1; j < pts.length; j++) {
          const d = Math.hypot(pts[i].x - pts[j].x, pts[i].y - pts[j].y);
          if (d < 110) { ctx.beginPath(); ctx.moveTo(pts[i].x, pts[i].y); ctx.lineTo(pts[j].x, pts[j].y); ctx.strokeStyle = `rgba(100,118,255,${.065 * (1 - d / 110)})`; ctx.lineWidth = .4; ctx.stroke(); }
        }
      pts.forEach(p => {
        const d = Math.hypot(mx - p.x, my - p.y);
        if (d < 100) { p.vx -= (mx - p.x) / d * .032; p.vy -= (my - p.y) / d * .032; }
        p.vx *= .99; p.vy *= .99; p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${p.h},72%,70%,${p.a})`; ctx.fill();
      });
      const g = ctx.createRadialGradient(mx, my, 0, mx, my, 170);
      g.addColorStop(0, 'rgba(99,102,241,0.05)'); g.addColorStop(1, 'transparent');
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      raf.current = requestAnimationFrame(draw);
    };
    draw();
    return () => { window.removeEventListener('resize', resize); window.removeEventListener('mousemove', mm); cancelAnimationFrame(raf.current); };
  }, []);

  return <canvas ref={ref} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: .6 }} />;
};

/* ═══════════════════════════════════════════════════════════════════
   SHARED PRIMITIVES
═══════════════════════════════════════════════════════════════════ */
const SectionHeader = ({ label, accent, icon: Icon, linkTo, linkLabel = 'View All' }) => (
  <div className="hp-sh">
    <div className="hp-sh-left">
      <div className="hp-sh-bar" style={{ background: `linear-gradient(to bottom,${accent},transparent)` }} />
      {Icon && <Icon size={13} color={accent} />}
      <span className="hp-sh-label" style={{ color: accent }}>{label}</span>
    </div>
    {linkTo && (
      <Link to={linkTo} className="hp-sh-link">
        {linkLabel} <ExternalLink size={10} />
      </Link>
    )}
  </div>
);

const ac = (cat) => cat === 'east' ? '#dc2626' : cat === 'west' ? '#06b6d4' : '#a78bfa';

const HeroArticleCard = ({ article, category, delay = 0 }) => {
  if (!article) return null;
  const color = ac(category);
  return (
    <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .55, delay }}>
      <Link to={`/article/${article.slug}`} className="hp-hero-card">
        {article.featured_image
          ? <img src={article.featured_image} alt={article.title} />
          : <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg,#0f0f1a,#1a1a2e)' }} />}
        <div className="hp-hero-card-grad" />
        <div className="hp-feat-badge" style={{ background: color, color: '#fff', boxShadow: `0 0 14px ${color}55` }}>
          Featured
        </div>
        <div className="hp-hero-card-body">
          <span className="hp-hero-card-cat" style={{ color }}>{article.category || category}</span>
          <h3 className="hp-hero-card-title">{article.title}</h3>
          <div className="hp-hero-card-meta">
            {article.author && <span><Users size={10} />{article.author}</span>}
            <span><Clock size={10} />{article.read_time || 5} min</span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

const SideCard = ({ article, rank, category, delay = 0 }) => {
  if (!article) return null;
  const color = ac(category);
  return (
    <motion.div initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: .4, delay }}>
      <Link to={`/article/${article.slug}`} className="hp-side-card">
        <div className="hp-side-thumb">
          {article.featured_image
            ? <img src={article.featured_image} alt={article.title} />
            : <div style={{ width: '100%', height: '100%', background: '#111' }} />}
          {rank && <div className="hp-side-rank" style={{ background: color }}>{rank}</div>}
        </div>
        <div className="hp-side-body">
          <div style={{ fontSize: '8px', color, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '3px', fontFamily: 'var(--az)', fontWeight: 700 }}>
            {article.category || category}
          </div>
          <p className="hp-side-title">{article.title}</p>
          <div style={{ fontSize: '9px', color: 'rgba(255,255,255,.28)', display: 'flex', alignItems: 'center', gap: '3px', fontFamily: 'var(--ui)' }}>
            <Clock size={8} />{article.read_time || 5} min
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

const GridCard = ({ article, category, delay = 0 }) => {
  if (!article) return null;
  const color = ac(category || article?.category);
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .38, delay }}>
      <Link to={`/article/${article.slug}`} className="hp-grid-card">
        <div className="hp-grid-card-img">
          {article.featured_image
            ? <img src={article.featured_image} alt={article.title} />
            : <div style={{ width: '100%', aspectRatio: '16/9', background: `${color}14`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Play size={18} color={color} opacity={.4} />
              </div>}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top,rgba(0,0,0,.42),transparent)' }} />
        </div>
        <div className="hp-grid-card-body">
          <div style={{ fontSize: '8px', color, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '5px', fontFamily: 'var(--az)', fontWeight: 700 }}>
            {article.category || category}
          </div>
          <p className="hp-grid-card-title">{article.title}</p>
          <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '9px', color: 'rgba(255,255,255,.26)', fontFamily: 'var(--ui)' }}>
            <Clock size={8} />{article.read_time || 5} min
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   PORTAL CARD
═══════════════════════════════════════════════════════════════════ */
const PortalCard = ({ portal, index }) => {
  const isEast = portal.id === 'east';
  const color = isEast ? '#dc2626' : '#06b6d4';
  const tintBg = isEast
    ? 'linear-gradient(135deg,rgba(220,38,38,.07) 0%,transparent 58%)'
    : 'linear-gradient(135deg,rgba(6,182,212,.07) 0%,transparent 58%)';

  return (
    <motion.div initial={{ opacity: 0, y: 36 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .65, delay: index * .13 }}>
      <Link to={portal.path} className={`hp-portal hp-portal-${portal.id}`}>
        {/* Background Image */}
        <div 
          style={{ 
            position: 'absolute', 
            inset: 0, 
            borderRadius: '22px',
            backgroundImage: isEast 
              ? "url('https://i.pinimg.com/1200x/2d/50/93/2d509399aceab6e117c84583a5b1679f.jpg')" 
              : "url('https://i.pinimg.com/1200x/32/a7/dc/32a7dc5ab50029a87761aec1ad3533ae.jpg')",
            backgroundSize: 'cover', 
            backgroundPosition: 'center',
            pointerEvents: 'none',
          }} 
        />
        {/* Black Gradient Overlay - Left to Right */}
        <div style={{ 
          position: 'absolute', 
          inset: 0, 
          borderRadius: '22px',
          background: 'linear-gradient(to right, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.75) 50%, rgba(0,0,0,0.4) 100%)',
          pointerEvents: 'none',
        }} />
        <div className="hp-portal-tint" style={{ background: tintBg }} />
        <div className="hp-portal-accent-line" style={{ background: `linear-gradient(to right,transparent,${color},transparent)` }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
            <div>
              <div style={{ fontSize: '8px', letterSpacing: '3px', color: 'rgba(255,255,255,.28)', textTransform: 'uppercase', marginBottom: '5px', fontFamily: 'var(--ui)' }}>Portal</div>
              <h2 className={isEast ? 'hp-portal-title-east' : 'hp-portal-title-west'}>{portal.title}</h2>
              <p style={{ fontSize: '12px', color: 'rgba(255,255,255,.42)', marginTop: '3px', fontFamily: 'var(--ui)' }}>{portal.subtitle}</p>
            </div>
            <div style={{ width: '38px', height: '38px', borderRadius: '50%', border: '1px solid rgba(255,255,255,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,.04)', flexShrink: 0 }}>
              <ArrowRight size={14} color={color} />
            </div>
          </div>
          <p style={{ fontSize: '13px', color: 'rgba(255,255,255,.35)', lineHeight: 1.72, marginBottom: '22px', fontFamily: 'var(--ui)' }}>{portal.description}</p>
          <div className="hp-portal-stats">
            {[
              { Ic: BookOpen, val: portal.stats.articles, lbl: 'Articles' },
              { Ic: Users,    val: portal.stats.readers,  lbl: 'Readers' },
              { Ic: TrendingUp, val: portal.stats.trending, lbl: 'Trending' },
            ].map((s, i) => (
              <div className="hp-portal-stat" key={i}>
                <s.Ic size={12} color={color} style={{ margin: '0 auto 4px', display: 'block' }} />
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#fff', fontFamily: 'var(--az)' }}>{s.val}</div>
                <div style={{ fontSize: '8px', color: 'rgba(255,255,255,.28)', letterSpacing: '1px', textTransform: 'uppercase', fontFamily: 'var(--ui)' }}>{s.lbl}</div>
              </div>
            ))}
          </div>
          <div className="hp-portal-cta" style={{ background: `linear-gradient(135deg,${color}18,${color}0c)`, border: `1px solid ${color}32`, color }}>
            Enter {portal.title} <ChevronRight size={12} />
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   TICKER
═══════════════════════════════════════════════════════════════════ */
const tickerItems = [
  { text: 'New Episode: Jujutsu Kaisen Season 3', color: '#dc2626' },
  { text: 'Trending: One Piece — Egghead Arc', color: '#dc2626' },
  { text: 'Now Streaming: Spider-Man 4 Confirmed', color: '#06b6d4' },
  { text: 'Review: Demon Slayer Hashira Training Arc', color: '#dc2626' },
  { text: 'Breaking: MCU Phase 6 Lineup Revealed', color: '#06b6d4' },
  { text: 'New Article: Top 10 Isekai of 2025', color: '#dc2626' },
  { text: 'Streamfinite: Watch Trailers & Discover', color: '#a78bfa' },
];

const Ticker = () => (
  <div className="hp-ticker">
    <div className="hp-ticker-inner">
      {[...tickerItems, ...tickerItems].map((t, i) => (
        <span className="hp-ticker-item" key={i}>
          <span className="hp-ticker-dot" style={{ background: t.color }} />
          {t.text}
        </span>
      ))}
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════
   ERROR BOUNDARY COMPONENT
═══════════════════════════════════════════════════════════════════ */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("HomePage Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px', textAlign: 'center', background: '#08080c', minHeight: '100vh', color: '#666' }}>
          <p>Something went wrong. Please refresh the page.</p>
          <button 
            onClick={() => window.location.reload()}
            style={{ marginTop: '20px', padding: '10px 20px', background: '#333', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
          >
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════════════ */
const HomePage = () => {
  const [email, setEmail]           = useState('');

  // Wrap everything in error boundary
  return (
    <ErrorBoundary>
      <HomePageContent email={email} setEmail={setEmail} />
    </ErrorBoundary>
  );
};

const HomePageContent = ({ email, setEmail }) => {
  const { articles: eastArticles }  = useArticles('east');
  const { articles: westArticles }  = useArticles('west');
  const { articles: allArticles }   = useArticles();
  const { featuredContent }         = useFeaturedContent();

  const portalData = [
    {
      id: 'east', title: 'EAST', subtitle: 'Anime & Manga Culture',
      description: 'Deep-dive features, trending news, and sharp analysis from the world of anime, manga, and Japanese pop culture.',
      path: '/outrange/east',
      stats: { articles: eastArticles?.length || 0, readers: '125K', trending: 'One Piece' },
    },
    {
      id: 'west', title: 'WEST', subtitle: 'Movies & Cartoons',
      description: 'Hollywood blockbusters, indie animations, and western cartoon classics — covered with sharp editorial perspective.',
      path: '/outrange/west',
      stats: { articles: westArticles?.length || 0, readers: '98K', trending: 'Spider-Verse' },
    },
  ];

  return (
    <>
      <PageStyles />
      <SEO
        title="Animac — Where Culture Meets Commentary"
        description="Anime, movies, cartoons and entertainment culture from East and West. The Animac Metaverse."
        url={typeof window !== 'undefined' ? window.location.href : '/'}
        image="/assets/Outrange black.png"
        type="website"
      />

      <ParticleBackground />

      <div className="hp-root">

        {/* ── HERO ── */}
        <AnimatePresence mode="wait">
          <motion.div
            key="hero"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            <HeroSection featuredContent={featuredContent} />
          </motion.div>
        </AnimatePresence>

        {/* ── TICKER ── */}
        <Ticker />

        <div className="hp-wrap">

          {/* ── PORTAL CARDS ── */}
          <section className="hp-sec">
            <SectionHeader label="Choose Your Portal" accent="#a78bfa" icon={Globe} />
            <div className="hp-portals">
              {portalData.map((p, i) => <PortalCard key={p.id} portal={p} index={i} />)}
            </div>
          </section>

          <div className="hp-div" />

          {/* ── EAST FEATURED ── */}
          {eastArticles?.length > 0 && (
            <section className="hp-sec">
              <SectionHeader label="East · Anime & Manga" accent="#dc2626" icon={Flame} linkTo="/outrange/east" />
              <div className="hp-feature-grid">
                <HeroArticleCard article={eastArticles[0]} category="east" />
                <div className="hp-side-col">
                  {eastArticles.slice(1, 5).map((a, i) => (
                    <SideCard key={a.id || i} article={a} rank={i + 1} category="east" delay={i * .06} />
                  ))}
                </div>
              </div>
              {eastArticles.length > 5 && (
                <div className="hp-grid hp-grid-4" style={{ marginTop: '10px' }}>
                  {eastArticles.slice(5, 9).map((a, i) => (
                    <GridCard key={a.id || i} article={a} category="east" delay={i * .04} />
                  ))}
                </div>
              )}
            </section>
          )}

          {/* ── STREAMFINITE PROMO BANNER ── */}
          <section className="hp-sec-sm">
            <div className="hp-stream-banner" style={{ position: 'relative', overflow: 'hidden', background: 'none' }}>
              {/* Background Image */}
              <div 
                style={{ 
                  position: 'absolute', 
                  inset: 0, 
                  borderRadius: '22px',
                  backgroundImage: "url('https://i.pinimg.com/1200x/b0/06/9b/b0069bb25cf7198ecd5f471ea151debe.jpg')", 
                  backgroundSize: 'cover', 
                  backgroundPosition: 'center',
                  pointerEvents: 'none',
                }} 
              />
              {/* Black Gradient Overlay */}
              <div style={{ 
                position: 'absolute', 
                inset: 0, 
                borderRadius: '22px',
                background: 'linear-gradient(to right, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0.3) 100%)',
                pointerEvents: 'none',
              }} />
              <div className="hp-stream-banner-glow" />
              <div style={{ position: 'relative', zIndex: 1 }}>
                <p className="hp-stream-eyebrow"><Tv size={11} style={{ display: 'inline', marginRight: 6 }} />Streamfinite</p>
                <h2 className="hp-stream-title">Watch Trailers.<br />Discover What's Next.</h2>
                <p className="hp-stream-desc">Browse trending anime, upcoming blockbusters, and top-rated shows — with real trailers from AniList, Jikan, TMDB & Trakt.</p>
                <div className="hp-stream-badge-row">
                  {['Anime', 'Movies', 'TV Shows', 'Real Trailers'].map((b) => (
                    <span className="hp-stream-badge" key={b}><Sparkles size={10} />{b}</span>
                  ))}
                </div>
              </div>
              <Link to="/streamfinite" className="hp-stream-enter" style={{ position: 'relative', zIndex: 1 }}>
                Enter Streamfinite <ArrowUpRight size={14} />
              </Link>
            </div>
          </section>

          <div className="hp-div" />

          {/* ── WEST FEATURED ── */}
          {westArticles?.length > 0 && (
            <section className="hp-sec">
              <SectionHeader label="West · Movies & Cartoons" accent="#06b6d4" icon={Star} linkTo="/outrange/west" />
              <div className="hp-feature-grid hp-feature-grid-rev">
                <div className="hp-side-col">
                  {westArticles.slice(1, 5).map((a, i) => (
                    <SideCard key={a.id || i} article={a} rank={i + 1} category="west" delay={i * .06} />
                  ))}
                </div>
                <HeroArticleCard article={westArticles[0]} category="west" />
              </div>
              {westArticles.length > 5 && (
                <div className="hp-grid hp-grid-4" style={{ marginTop: '10px' }}>
                  {westArticles.slice(5, 9).map((a, i) => (
                    <GridCard key={a.id || i} article={a} category="west" delay={i * .04} />
                  ))}
                </div>
              )}
            </section>
          )}

          <div className="hp-div" />

          {/* ── BROWSE ALL ── */}
          {allArticles?.length > 0 && (
            <section className="hp-sec">
              <SectionHeader label="Browse All" accent="#a78bfa" icon={TrendingUp} linkTo="/outrange" />
              <div className="hp-grid hp-grid-4">
                {allArticles.slice(0, 12).map((a, i) => (
                  <GridCard key={a.id || i} article={a} category={a.category} delay={i * .025} />
                ))}
              </div>
            </section>
          )}

          {/* ── CONTENT ROWS (preserved) ── */}
          {eastArticles?.length > 0 && (
            <section className="hp-sec-sm">
              <SectionHeader label="This Week in Anime" accent="#dc2626" icon={BookOpen} />
              <ContentRow title="Otaku Updates" articles={eastArticles} category="east"
                emptyMessage="Discover the latest anime releases, manga chapters, and Japanese entertainment news." />
            </section>
          )}

          {westArticles?.length > 0 && (
            <section className="hp-sec-sm">
              <SectionHeader label="Movies & Cartoons Spotlight" accent="#06b6d4" icon={Play} />
              <ContentRow title="Binge Updates" articles={westArticles} category="west"
                emptyMessage="Explore Hollywood blockbusters, Disney animations, and streaming series reviews." />
            </section>
          )}

          <div className="hp-div" />

          {/* ── WATCHTOWER PREVIEW ── */}
          <section className="hp-sec-sm">
            <SectionHeader label="Streamfinite Preview" accent="#6366f1" icon={Tv} linkTo="/streamfinite" linkLabel="Open" />
            <WatchTowerPreview />
          </section>

          <div className="hp-div" />

          {/* ── COMMUNITY ── */}
          <section className="hp-sec">
            <SectionHeader label="Community" accent="#a78bfa" icon={Users} linkTo="/community" linkLabel="View All" />
            <div className="hp-community-grid">
              <CommunityEvents />
              <CommunityFeatures />
            </div>
          </section>

          <div className="hp-div" />

          {/* ── NEWSLETTER ── */}
          <section className="hp-sec-sm">
            <div className="hp-nl">
              <div className="hp-nl-glow" />
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ fontSize: '9px', fontFamily: 'var(--az)', letterSpacing: '3px', textTransform: 'uppercase', color: 'var(--neu)', marginBottom: '10px' }}>
                  <Mail size={10} style={{ display: 'inline', marginRight: 6 }} />Stay in the Culture
                </div>
                <h3 style={{ fontFamily: 'var(--az)', fontSize: 'clamp(18px,3vw,28px)', color: '#fff', marginBottom: '8px' }}>
                  Never Miss a Drop
                </h3>
                <p style={{ fontSize: '14px', color: 'rgba(255,255,255,.38)', fontFamily: 'var(--bd)', lineHeight: 1.7, maxWidth: '420px', margin: '0 auto' }}>
                  Weekly dispatches from East and West — anime picks, movie reviews, and cultural commentary.
                </p>
                <form className="hp-nl-form" onSubmit={(e) => e.preventDefault()}>
                  <input
                    className="hp-nl-input"
                    type="email" placeholder="your@email.com"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                  />
                  <button className="hp-nl-btn" type="submit">Subscribe</button>
                </form>
              </div>
            </div>
          </section>

          {/* ── FOOTER CTA ── */}
          <motion.section
            initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: .65, delay: .2 }}
            className="hp-sec"
          >
            <div className="hp-footer-cta">
              <div className="hp-footer-cta-glow" />
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ fontSize: '26px', marginBottom: '12px' }}>✦</div>
                <h3 style={{ fontFamily: 'var(--az)', fontSize: 'clamp(18px,3.2vw,28px)', color: '#fff', marginBottom: '10px' }}>
                  Pick Your World
                </h3>
                <p style={{ fontSize: '14px', color: 'rgba(255,255,255,.36)', lineHeight: 1.8, fontFamily: 'var(--bd)', maxWidth: '420px', margin: '0 auto 24px' }}>
                  East or West, anime or blockbuster — the Animac Metaverse has a portal for every culture.
                </p>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Link to="/outrange/east" className="hp-ghost-link" style={{ borderColor: 'rgba(220,38,38,.32)', color: '#dc2626' }}>
                    East Portal <ChevronRight size={12} />
                  </Link>
                  <Link to="/outrange/west" className="hp-ghost-link" style={{ borderColor: 'rgba(6,182,212,.32)', color: '#06b6d4' }}>
                    West Portal <ChevronRight size={12} />
                  </Link>
                </div>
              </div>
            </div>
          </motion.section>

        </div>
      </div>
    </>
  );
};

export default HomePage;